import { Calendar, CheckCircle, Clock, File, Mail } from "lucide-react";
import { GetSnapshotEventResponseItem } from "@/modules/snapshots/businessReports.types";
import { clsx } from "clsx";
import { formatDateWithLocale } from "@/helpers/dates";
import { useSnapshotEventsContext } from "@/modules/snapshots/snapshotEvents/useSnapshotEventsContext";

const STATUS_LABEL_MAP = {
  overdue: "Overdue",
  done: "Completed",
  in_progress: "In Progress",
} as const;

const DAY = 24 * 60 * 60 * 1000;

interface Props {
  event: GetSnapshotEventResponseItem;
}

export const SnapshotEventCard = ({ event }: Props) => {
  const {
    setSelectedEvent,
    setSelectedEventToEdit,
    getCompletionRate,
    sendReminderToRecipient,
    sendRemindersToAllRecipients,
    makeSnapshotEventCompleted,
    makeSnapshotEventRecipientCompleted,
  } = useSnapshotEventsContext();

  const isOverdue = event.status === "in_progress" && +new Date(event.event_date) + DAY < Date.now();
  const daysUntil = Math.abs((+new Date(event.event_date) - Date.now()) / (1000 * 60 * 60 * 24));
  const status = isOverdue ? "overdue" : event.status;

  const total = event.required_only_one_done ? 1 : event.recipients.filter(recipient => !recipient.is_readonly).length;
  const completedCount = event.recipients.filter(({ status }) => status === "done").length;

  return (
    <div className="rounded-lg border bg-white p-6 shadow-sm" style={{ borderColor: "#e9e0d5" }}>
      <div className="flex flex-col gap-6">
        <div className="flex-1">
          <div className="mb-4 flex items-start justify-between">
            <div>
              <div className="flex items-center gap-2">
                <h3 className="text-xl font-semibold" style={{ color: "#0a213e" }}>
                  {event.title}
                </h3>
                <span className="text-sm text-gray-700">{event.event_type}</span>
              </div>
              <div className="flex items-center gap-4 text-sm" style={{ color: "#8b92af" }}>
                <div className="flex items-center gap-1">
                  <Calendar size={16} />
                  {formatDateWithLocale(event.event_date, "Verbose")}
                </div>
                <div className="flex items-center gap-1">
                  <Clock size={16} />
                  {isOverdue ? `${Math.round(daysUntil)} days overdue` : `${Math.round(daysUntil)} days until`}
                </div>
              </div>

              <div className="flex items-center gap-4 text-sm" style={{ color: "#8b92af" }}>
                <div className="flex items-center gap-1">
                  <File size={16} />
                  {event.document_name.slice(0, 128)}
                </div>
              </div>
            </div>
            <span
              className={clsx("rounded-full px-3 py-1 text-sm font-medium text-white", {
                "bg-[#dc2626]": isOverdue,
                "bg-[#16a34a]": event.status === "done",
                "bg-[#8b92af]": event.status !== "done" && !isOverdue,
              })}
            >
              {STATUS_LABEL_MAP[status]}
            </span>
          </div>

          <div className="mb-4 flex items-center gap-2">
            {event.status !== "done" && (
              <>
                <button
                  onClick={() => makeSnapshotEventCompleted(event.id)}
                  className="cursor-pointer rounded px-3 py-1 text-sm font-medium transition-colors"
                  style={{
                    backgroundColor: "#0a213e",
                    color: "#ffffff",
                  }}
                >
                  Mark As Complete
                </button>
                <button
                  className="rounded px-3 py-1 text-sm font-medium transition-colors"
                  onClick={() => sendRemindersToAllRecipients(event.id)}
                  style={{
                    backgroundColor: "#F1F5F9",
                    color: "#3c3c41",
                  }}
                >
                  Send Reminders
                </button>
              </>
            )}
          </div>

          <div className="mb-4 flex items-center gap-6">
            <div className="text-sm">
              <span style={{ color: "#3c3c41" }}>Progress: </span>
              <span className="font-medium" style={{ color: "#0a213e" }}>
                {completedCount}/{total} completed ({getCompletionRate(event)}%)
              </span>
            </div>
            <div className="text-sm">
              <span style={{ color: "#3c3c41" }}>Total Recipients: </span>
              <span className="font-medium" style={{ color: "#0a213e" }}>
                {event.recipients.length}
              </span>
            </div>
            <div className="text-sm">
              <span style={{ color: "#3c3c41" }}>Completion Type: </span>
              <span
                className="font-bold"
                style={{
                  color: "#000000",
                }}
              >
                {event.required_only_one_done ? "Any One Person" : "All Required"}
              </span>
            </div>
          </div>

          <div className="mb-4 h-3 w-full rounded-full bg-gray-200">
            <div
              className="h-3 rounded-full transition-all duration-300"
              style={{
                width: `${getCompletionRate(event)}%`,
                backgroundColor: isOverdue ? "#dc2626" : "#16a34a",
              }}
            ></div>
          </div>
        </div>

        <div className="flex-1">
          <div className="mb-3 flex items-center justify-between">
            <h4 className="font-medium" style={{ color: "#0a213e" }}>
              Individual Progress
            </h4>

            <div className="flex items-center gap-2">
              {event.status !== "done" && (
                <button
                  className="rounded px-3 py-1 text-sm font-medium transition-colors"
                  onClick={() => setSelectedEventToEdit(event)}
                  style={{
                    backgroundColor: "#F1F5F9",
                    color: "#3c3c41",
                  }}
                >
                  Edit Alert
                </button>
              )}
              <button
                className="rounded px-3 py-1 text-sm font-medium transition-colors"
                onClick={() => setSelectedEvent(event)}
                style={{
                  backgroundColor: "#F1F5F9",
                  color: "#3c3c41",
                }}
              >
                View Alert
              </button>
              <a
                className="rounded px-3 py-1 text-sm font-medium transition-colors"
                href={`/reports/outcome/${event.business_report_outcome_id}`}
                style={{
                  backgroundColor: "#F1F5F9",
                  color: "#3c3c41",
                }}
              >
                Snapshot
              </a>
              <a
                className="rounded px-3 py-1 text-sm font-medium transition-colors"
                href={`/chat/${event.fse_id}`}
                style={{
                  backgroundColor: "#F1F5F9",
                  color: "#3c3c41",
                }}
              >
                Q&A
              </a>
            </div>
          </div>
          <div className="max-h-40 space-y-2 overflow-y-auto">
            {event.recipients.map((recipient, index) => {
              const actionRequired = recipient.status === "in_progress" && event.status === "in_progress";
              return (
                <div
                  key={index}
                  className="flex items-center justify-between rounded p-2"
                  style={{ backgroundColor: "#efebe7" }}
                >
                  <div className="flex items-center gap-2">
                    <span className="text-sm" style={{ color: "#3c3c41" }}>
                      {recipient.email}
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    {actionRequired ? (
                      <>
                        {recipient.status === "done" ? (
                          <>
                            <CheckCircle size={16} className="text-success" />
                            <span className="text-xs" style={{ color: "#8b92af" }}>
                              {recipient.completion_date}
                            </span>
                          </>
                        ) : (
                          <>
                            <button
                              onClick={() => sendReminderToRecipient(recipient.id)}
                              className="rounded px-2 py-1 text-xs font-medium transition-colors"
                              style={{
                                backgroundColor: "#F1F5F9",
                                color: "#3c3c41",
                              }}
                            >
                              Send Reminder
                            </button>
                            {!recipient.is_readonly && (
                              <button
                                className="rounded px-2 py-1 text-xs font-medium transition-colors"
                                style={{
                                  backgroundColor: "#0a213e",
                                  color: "#ffffff",
                                }}
                                onClick={() => makeSnapshotEventRecipientCompleted(recipient.id)}
                              >
                                Mark Complete
                              </button>
                            )}
                          </>
                        )}
                      </>
                    ) : (
                      recipient.status === "done" && (
                        <>
                          <Mail size={16} style={{ color: "#8b92af" }} />
                          <span className="text-xs" style={{ color: "#8b92af" }}>
                            Completed
                          </span>
                        </>
                      )
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};
